import React, { type ReactNode } from 'react';

import type { MaybeChoreographedComponentProps } from '@jira/platform__choreographer-services/src/types.tsx';
import { shouldBeChoreographed } from '@jira/platform__choreographer-services/src/utils.tsx';
import { withChoreographedRender } from '@jira/platform__choreographer-services/src/withChoreographedRender.tsx';
import { componentWithFG } from '@jira/platform__utils-feature-gates-gate-component/src/index.tsx';

type ChildrenWrapperProps = { children: ReactNode };

const ChildrenWrapper = ({ children }: ChildrenWrapperProps) => <>{children}</>;
const ChoreographedChildrenWrapper = withChoreographedRender(ChildrenWrapper);

type ChoreographedComponentProps = MaybeChoreographedComponentProps & { children: ReactNode };

/**
 * ChoreographedComponent wraps children with the Choreographer service to coordinate
 * multiple messages on the frontend so that only one appears at a time.
 */
export const ChoreographedComponent = ({
	messageType,
	messageId,
	onMessageDisposition,
	children,
}: ChoreographedComponentProps): React.JSX.Element => {
	if (!shouldBeChoreographed({ messageId, messageType, onMessageDisposition })) {
		return <>{children}</>;
	}

	return (
		<ChoreographedChildrenWrapper messageId={messageId} onMessageDisposition={onMessageDisposition}>
			{children}
		</ChoreographedChildrenWrapper>
	);
};

/**
 * ChoreographedComponentWithFG wraps ChoreographedComponent with the
 * 'post-office_enable_choroegraphed_spotlight_card' feature gate.
 * When the gate is OFF, children render directly without choreography.
 * When the gate is ON, children are wrapped with ChoreographedComponent.
 */
export const ChoreographedComponentWithFG: React.ComponentType<ChoreographedComponentProps> =
	componentWithFG(
		'post-office_enable_choroegraphed_spotlight_card',
		ChoreographedComponent,
		ChildrenWrapper,
	);
